import "./styles.css";
import { FC, useState, useContext, useEffect } from "react";
import { useFormik } from "formik";
import { Box, Grid } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import useMutation from "../../../../../shared/components/hooks/useMutation";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import { SidebarContext } from "../../../../../contexts/SidebarContext";
import InputTextfield from "../../../../../shared/components/textField";
import { FormContainer } from "../../../../../shared/components/formContainer";
import { ViewContainer } from "../../../../../shared/components/viewContainer";
import SingleEntityContainer from "../../../../../shared/components/singleEntityContainer";
import { TitleBarProps } from "../../../../../shared/components/titleBar";
import { getTextFields } from "../variables/textFields";
import { customizeValidationSchema } from "../variables/customSchema";
import { Industry } from "../../../../../shared/interfaces/industry";
import ToggleSwitch from "../../../../../shared/components/switch";
import useLazyQuery from "../../../../../shared/components/hooks/useLazyQuery";
import { ZSAutocomplete } from "../../../../../shared/components/autoComplete";
import { EditButton } from "../../../../../shared/components/editButton";
import { CloseButton } from "../../../../../shared/components/closeButton";
import { ProfileBorderBox } from "../../../../../shared/components/profileComponents";
import { Skill } from "../../../../../shared/interfaces/skill";

const SkillEditView: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, data, fetchData } = useQuery(`/skill/${id}`, null);
  const { loading: apiLoading, modifyData } = useMutation();
  const [editStatus, setEditStatus] = useState(false);
  const skillData: Skill = (data as any)?.data || {};
  const { payload, setPayload } = useContext(SidebarContext);

  const { data: industries, fetchData: IndustriesFetchData } = useLazyQuery(
    "/industry",
    {
      params: {
        ...payload,
        take: 25,
        sortOrder: "ASC",
        enabled: true,
      },
    }
  );
  useEffect(() => {
    if (editStatus) {
      IndustriesFetchData();
    }
  }, [editStatus]);
  const industriesData: Industry[] = (industries as any)?.items || [];
  const selectedRole =
    skillData && skillData?.industry?.id
      ? {
          value: skillData?.industry?.id,
          label: skillData?.industry?.name,
        }
      : null;
  const industriesOption = industriesData?.map((industry: Industry) => ({
    value: industry.id,
    label: industry.name,
  }));
  //Edit permission checking
  const entityPermissions: any = (data as any)?.entityPermissions;
  const editPermission = entityPermissions
    ? entityPermissions?.includes("Skill-Update")
    : true;
  //End of edit permission checking

  const initialValues = {
    name: skillData?.name ?? "",
    enabled: skillData?.enabled ? true : false,
    industryId: skillData?.industry?.id ?? "",
  };

  const skillForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,

    validationSchema: customizeValidationSchema(),

    onSubmit: async (formData: any) => {
      let response;
      if (id) {
        response = await modifyData(
          `/skill/${id}`,
          "put",
          {
            ...formData,
          },
          skillForm
        );
      }
      if (response) {
        closeEdit();
      }
    },
  });

  const closeEdit = () => {
    fetchData();
    setEditStatus(false);
  };

  const editTitlebar: TitleBarProps = {
    headerTitle: "Skill -  Edit",
    title: "Edit Skill",
    buttonTitle: "Save",
    onClick: () => skillForm.handleSubmit(),
    dirty: skillForm.dirty,
    buttonLoading: apiLoading,
    editCloseIcon: {
      visible: true,
      onClick: () => closeEdit(),
    },
  };

  const viewTitleBar: TitleBarProps = {
    title: "Skill Details",
    headerTitle: "Skill Details",
  };
  const skillEditFields = getTextFields(skillData);

  const closeDetailsView = () => {
    navigate("/master-datas/skills");
  };

  const skillEditTooltipNode = (
    <>
      {editPermission && <EditButton onClick={() => setEditStatus(true)} />}
      <CloseButton onClick={() => closeDetailsView()} />
    </>
  );

  return (
    <>
      {editStatus && (
        <>
          <ProfileBorderBox>
            <FormContainer titleBar={editTitlebar}>
              <Box className="form-border-custom">
                <Grid
                  container
                  alignItems="stretch"
                  columnSpacing={4}
                  rowSpacing={2}
                >
                  <Grid item xs={12}>
                    <ToggleSwitch
                      status={skillForm?.values?.enabled}
                      handleChange={() =>
                        skillForm.setFieldValue(
                          "enabled",
                          !skillForm?.values?.enabled
                        )
                      }
                    />
                  </Grid>
                  {skillEditFields.map(
                    (field: any, index: number) =>
                      (field?.formField === undefined ||
                        field.formField === true) && (
                        <Grid item md={6} sm={6} xs={12} key={field.id}>
                          <InputTextfield
                            form={skillForm}
                            fieldProps={field}
                            key={index}
                          />
                        </Grid>
                      )
                  )}
                  <Grid item md={6} sm={6} xs={12}>
                    <ZSAutocomplete
                      options={industriesOption}
                      label="Industry"
                      placeholder="Select Industry"
                      defaultValue={selectedRole}
                      required
                      setValue={(selectedOption: any) => {
                        skillForm.setFieldTouched("industryId", true);
                        if (selectedOption?.value) {
                          skillForm.setFieldValue(
                            "industryId",
                            selectedOption?.value
                          );
                        } else {
                          skillForm.setFieldValue("industryId", []);
                        }
                      }}
                      form={{ ...skillForm, name: "industryId" }}
                      refetchFunction={(searchString: string) => {
                        setPayload((prevData: any) => {
                          return { ...prevData, search: searchString };
                        });
                      }}
                      setBlur={() => {
                        skillForm.setFieldTouched("industryId", true);
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </FormContainer>
          </ProfileBorderBox>
        </>
      )}

      {!editStatus && (
        <ProfileBorderBox>
          <ViewContainer loading={loading} titleBar={viewTitleBar}>
            <div className="entity-container">
              <SingleEntityContainer
                entityFields={skillEditFields}
                tooltips={skillEditTooltipNode}
              ></SingleEntityContainer>
            </div>
          </ViewContainer>
        </ProfileBorderBox>
      )}
    </>
  );
};

export default SkillEditView;
